import React, { type JSX } from "react";
import type { ConversationDetail, ConversationSession } from "./types";
import type { EnhancedTurn } from "./useConversationDetail";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { channelMeta } from "./channelMeta";
import { formatDateWithZone } from "../../utils/formatDate";
import { formatDuration, environmentClass } from "./conversationFormatting";
import CopyConversationButton from "./conversations/CopyConversationButton";

const BADGE = "inline-flex px-2 py-1 text-xs font-medium rounded-full";

function Field({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-500">{label}</label>
      {children}
    </div>
  );
}

function CostField({ detail }: { detail: ConversationDetail }): JSX.Element {
  const tokens = (detail.total_prompt_tokens || 0) + (detail.total_completion_tokens || 0);

  return (
    <Field label="Total Cost">
      <div className="mt-1">
        <p className="text-sm font-medium text-gray-900">
          {formatCost(getTotalCostFromApi(detail.total_input_cost_usd, detail.total_output_cost_usd))}
        </p>
        {(detail.total_prompt_tokens || detail.total_completion_tokens) && (
          <p className="text-xs text-gray-500">{tokens.toLocaleString()} tokens</p>
        )}
      </div>
    </Field>
  );
}

function StatusField({
  detail,
  hasErrors,
}: {
  detail: ConversationDetail;
  hasErrors: boolean;
}): JSX.Element {
  return (
    <Field label="Status">
      <div className="flex items-center space-x-2 mt-1">
        {detail.hit_limits && (
          <span className={`${BADGE} bg-orange-100 text-orange-800`}>Rate Limited</span>
        )}
        {hasErrors && <span className={`${BADGE} bg-red-100 text-red-800`}>Has Errors</span>}
        {!detail.hit_limits && !hasErrors && (
          <span className={`${BADGE} bg-green-100 text-green-800`}>Normal</span>
        )}
      </div>
    </Field>
  );
}

function SummaryGrid({
  detail,
  sessionCount,
  hasErrors,
}: {
  detail: ConversationDetail;
  sessionCount: number;
  hasErrors: boolean;
}): JSX.Element {
  const meta = channelMeta(detail.channel_id);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      <Field label="Project ID" className="xl:col-span-2">
        <p className="mt-1 text-sm font-mono text-gray-900">
          {detail.project_id || detail.conversation_id}
        </p>
      </Field>
      <Field label="Username">
        <p className="mt-1 text-sm text-gray-900">{detail.username}</p>
      </Field>
      <Field label="Entra Object ID" className="xl:col-span-2">
        <p className="mt-1 text-sm font-mono text-gray-600">
          {detail.entra_oid || <span className="text-gray-400">Not available</span>}
        </p>
      </Field>
      <Field label="Environment">
        <span className={`mt-1 ${BADGE} ${environmentClass(detail.environment)}`}>
          {detail.environment}
        </span>
      </Field>
      <Field label="Source">
        <span className={`mt-1 ${BADGE} ${meta.cls}`}>{meta.label}</span>
      </Field>
      <Field label="Duration">
        <div className="flex items-center space-x-2 mt-1">
          <p className="text-sm text-gray-900">{formatDuration(detail.total_time_ms)}</p>
          {detail.hit_limits && (
            <span className="text-orange-600" title="Rate limits were hit during this conversation">
              {"🚫"}
            </span>
          )}
        </div>
      </Field>
      <CostField detail={detail} />
      <Field label="Sessions">
        <p className="mt-1 text-sm text-gray-900">{sessionCount}</p>
      </Field>
      <Field label="Created">
        <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(detail.created_at)}</p>
      </Field>
      <StatusField detail={detail} hasErrors={hasErrors} />
    </div>
  );
}

interface ConversationSummaryProps {
  detail: ConversationDetail;
  sessions: ConversationSession[];
  turns: EnhancedTurn[];
  error: string | null;
}

/** Header card: conversation metadata, cost, status and an error summary. */
export default function ConversationSummary({
  detail,
  sessions,
  turns,
  error,
}: ConversationSummaryProps): JSX.Element {
  const erroredTurns = turns.filter((turn) => turn.error);

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">Project Conversation Details</h2>
          <CopyConversationButton
            conversation={{
              conversation_id: detail.conversation_id,
              username: detail.username,
            }}
            showLabel
          />
        </div>
        {error && (
          <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
            API error - {error}
          </div>
        )}
      </div>

      <SummaryGrid
        detail={detail}
        sessionCount={sessions.length}
        hasErrors={erroredTurns.length > 0}
      />

      {detail.updated_at && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-500">Last Updated</label>
          <p className="mt-1 text-sm text-gray-900">{formatDateWithZone(detail.updated_at)}</p>
        </div>
      )}

      {/* Error Summary */}
      {erroredTurns.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center">
            <span className="text-red-600 mr-2 text-lg">{"⚠️"}</span>
            <span className="text-sm font-medium text-red-800">
              This project has {erroredTurns.length} turn(s) with errors
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
